import React from 'react';
import { Link } from 'react-router-dom';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

interface RequireFeatureFlagProps {
  featureKey: string;
  children: React.ReactNode;
}

// Guards a route behind a platform feature flag so a direct/bookmarked URL
// doesn't reach a page whose API calls will just 403 — shows a friendly
// placeholder instead. Complements requireFeatureFlag on the backend and the
// nav-hiding in Layout.tsx, which only hide the entry point, not the route.
const RequireFeatureFlag: React.FC<RequireFeatureFlagProps> = ({ featureKey, children }) => {
  const { isFeatureEnabled, isLoading } = useFeatureFlags();

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isFeatureEnabled(featureKey)) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <div className="h-14 w-14 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
          <SparklesIcon className="h-7 w-7 text-blue-600 dark:text-blue-400" />
        </div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">This feature isn't available yet</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
          We're still putting the finishing touches on it. Check back soon.
        </p>
        <Link
          to="/app"
          className="mt-6 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
        >
          Back to Dashboard
        </Link>
      </div>
    );
  }

  return <>{children}</>;
};

export default RequireFeatureFlag;
